import { Eye, EyeOff, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { businessApplications as api } from "../lib/api";
import { Modal } from "./Modal";

// Staff viewer/editor for a submitted Business Funding Application.
// - SSNs come MASKED from the server; the eye button refetches with reveal=1
//   (the reveal is written to the client's event log server-side).
// - The pencil switches to edit mode (owner/manager — backend enforces).

const PERSONAL: Array<[string, string]> = [
  ["first_name", "First name"], ["last_name", "Last name"], ["email", "Email"], ["phone", "Phone"],
  ["dob", "Date of birth"], ["ssn", "Social Security Number"], ["address", "Address"], ["city", "City"],
  ["state", "State"], ["postal_code", "Postal code"], ["country", "Country"],
  ["annual_household_income", "Annual household income"], ["job_title", "Job title in company"],
];
const BUSINESS: Array<[string, string]> = [
  ["business_legal_name", "Legal business name"], ["business_name_on_card", "Name on card"],
  ["has_dba", "Has DBA?"], ["dba_name", "DBA / trade name"],
  ["business_address", "Business address"], ["business_city", "Business city"],
  ["business_state", "Business state"], ["business_zip", "Business ZIP"],
  ["business_phone", "Business phone"], ["business_start_date", "Business start date"],
  ["operations_in_us", "Operations in US?"], ["is_sole_owner", "Sole owner?"],
  ["business_type", "Type of business"], ["business_category", "Business category"],
  ["ein", "Tax ID (EIN)"], ["num_employees", "Employees"],
  ["annual_revenue", "Annual revenue"], ["years_in_business", "Years in business"],
];
// Fetch an r2:// document through the staff-authed proxy and open it in a new tab.
async function openDoc(r2url: string, onErr: (m: string) => void) {
  try {
    const key = String(r2url).replace(/^r2:\/\//, "");
    const token = localStorage.getItem("nedm.staff.token");
    const res = await fetch(`/api/uploads/${key}`, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
    if (!res.ok) throw new Error(`Could not open document (${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e: any) { onErr(e?.message || "Could not open document."); }
}

const OWNER_FIELDS: Array<[string, string]> = [
  ["first_name", "First name"], ["middle_name", "Middle name"], ["last_name", "Last name"],
  ["ssn", "SSN"], ["dob", "Date of birth"], ["percentage_owned", "% owned"],
  ["email", "Email"], ["address", "Address"],
];

export function BusinessAppModal({ appId, onClose }: { appId: string | null; onClose: () => void }) {
  const [meta, setMeta] = useState<any | null>(null);
  const [payload, setPayload] = useState<Record<string, any>>({});
  const [revealed, setRevealed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, any>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function load(reveal: boolean) {
    if (!appId) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.detail(appId, reveal);
      setMeta(r.application); setPayload(r.payload); setRevealed(r.revealed);
    } catch (e: any) { setMsg(e?.message || "Could not load the application."); }
    setBusy(false);
  }
  useEffect(() => { setEditing(false); setRevealed(false); if (appId) load(false); /* eslint-disable-next-line */ }, [appId]);

  async function startEdit() {
    // edit against the REAL values so a masked SSN is never saved back
    if (!revealed) await load(true);
    setDraft(JSON.parse(JSON.stringify(payloadRef() )));
    setEditing(true);
  }
  function payloadRef() { return payload; }

  async function save() {
    if (!appId) return;
    setBusy(true); setMsg(null);
    try {
      await api.update(appId, draft);
      setEditing(false);
      await load(revealed);
      setMsg("Saved.");
    } catch (e: any) { setMsg(e?.message || "Could not save changes."); }
    setBusy(false);
  }

  const view = editing ? draft : payload;
  const setField = (k: string, v: any) => setDraft((p) => ({ ...p, [k]: v }));
  const setOwnerField = (i: number, k: string, v: any) =>
    setDraft((p) => {
      const co = [...(p.co_owners || [])]; co[i] = { ...co[i], [k]: v };
      return { ...p, co_owners: co };
    });

  const fmt = (k: string, v: any) => {
    if (v === undefined || v === null || v === "") return "—";
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (k === "annual_household_income" || k === "annual_revenue") {
      const n = Number(v); return Number.isFinite(n) ? `$${n.toLocaleString()}` : String(v);
    }
    return String(v);
  };

  const row = (k: string, label: string, value: any, onChange?: (v: string) => void) => (
    <div key={k} className="profile-metrics" style={{ gridTemplateColumns: "1fr", gap: 2, margin: 0 }}>
      <div>
        <p style={{ margin: 0 }}>{label}</p>
        {editing && onChange ? (
          <input className="text-input" style={{ width: "100%", fontSize: 14 }} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
        ) : (
          <strong style={{ fontSize: 14 }}>{fmt(k, value)}</strong>
        )}
      </div>
    </div>
  );

  const owners: any[] = (view.co_owners || []).filter((o: any) => o && Object.values(o).some((v) => v));

  return (
    <Modal open={!!appId} title="Business Funding Application" onClose={onClose} wide>
      <div style={{ maxHeight: "72vh", overflowY: "auto", paddingRight: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <span className="muted small">
            {meta ? `${meta.client_name} · ${meta.status}${meta.submitted_at ? ` · submitted ${String(meta.submitted_at).slice(0, 10)}` : ""}` : "Loading…"}
          </span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {!editing && (
              <button className="ghost-button" title={revealed ? "Hide SSNs" : "Reveal SSNs"} aria-label={revealed ? "Hide SSNs" : "Reveal SSNs"} onClick={() => load(!revealed)} disabled={busy}>
                {revealed ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />} {revealed ? "Hide SSN" : "Reveal SSN"}
              </button>
            )}
            {!editing ? (
              <button className="ghost-button" title="Edit application" aria-label="Edit application" onClick={startEdit} disabled={busy}>
                <Pencil size={15} aria-hidden="true" /> Edit
              </button>
            ) : (
              <>
                <button className="primary-button" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save changes"}</button>
                <button className="ghost-button" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
              </>
            )}
          </div>
        </div>

        {msg && <p className="muted small" role="status" style={{ marginTop: 4 }}>{msg}</p>}

        <h3 className="section-title" style={{ marginTop: 12 }}>Personal</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 10 }}>
          {PERSONAL.map(([k, label]) => row(k, label, view[k], editing ? (v) => setField(k, v) : undefined))}
        </div>

        <h3 className="section-title" style={{ marginTop: 16 }}>Business</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 10 }}>
          {BUSINESS.map(([k, label]) => row(k, label, view[k], editing ? (v) => setField(k, v) : undefined))}
        </div>

        {owners.length > 0 && (
          <>
            <h3 className="section-title" style={{ marginTop: 16 }}>Co-owners</h3>
            {owners.map((o, i) => (
              <div key={i} style={{ border: "1px solid var(--ne-border, #333)", borderRadius: 10, padding: 10, marginBottom: 8 }}>
                <strong className="muted small">Owner {i + 1}</strong>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10, marginTop: 6 }}>
                  {OWNER_FIELDS.map(([k, label]) => row(`o${i}-${k}`, label, o[k], editing ? (v) => setOwnerField(i, k, v) : undefined))}
                </div>
              </div>
            ))}
          </>
        )}

        <h3 className="section-title" style={{ marginTop: 16 }}>Documents</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 10 }}>
          <div>
            <p style={{ margin: 0 }} className="muted small">Driver license / State ID</p>
            {view.owner_id_url
              ? <button className="ghost-button" style={{ fontSize: 13 }} onClick={() => openDoc(view.owner_id_url, setMsg)}>{view.owner_id_name || "View document"}</button>
              : <strong style={{ fontSize: 14 }}>—</strong>}
          </div>
          <div>
            <p style={{ margin: 0 }} className="muted small">Business documents</p>
            {(view.business_documents || []).length
              ? (view.business_documents || []).map((d: any, i: number) => (
                  <button key={i} className="ghost-button" style={{ fontSize: 13, marginRight: 6 }} onClick={() => openDoc(d.url, setMsg)}>{d.name || `Document ${i + 1}`}</button>
                ))
              : <strong style={{ fontSize: 14 }}>—</strong>}
          </div>
        </div>

        <h3 className="section-title" style={{ marginTop: 16 }}>Consent</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 10 }}>
          <div><p style={{ margin: 0 }} className="muted small">Agreed to terms</p><strong style={{ fontSize: 14 }}>{view.agree_terms ? "Yes" : "No"}</strong></div>
          <div><p style={{ margin: 0 }} className="muted small">Consented to communications</p><strong style={{ fontSize: 14 }}>{view.consent_comms ? "Yes" : "No"}</strong></div>
        </div>
      </div>
    </Modal>
  );
}

